import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useCart } from "../context/CartContext";
import { FaUser, FaPhone, FaCalendar, FaEnvelope, FaUsers, FaTrash, FaCheckCircle, FaMapMarkerAlt, FaSpinner } from "react-icons/fa";
import { motion } from "framer-motion";
import { API_URL, PAYMENT_URL } from "../config";
import { getItemPayable, getTravellerCount, getTotalPayable } from "../lib/pricing";
import "./CartNew.css";

function Cart() {
  const { cartItems, removeFromCart, clearCart } = useCart();
  const [isProcessing, setIsProcessing] = useState(false);

  /* Total to charge now, summed across all items in the cart */
  const totalPayable = getTotalPayable(cartItems);
  const isVisaOnly = cartItems.length > 0 && cartItems.every((item) => item?.type === "visa");

  /* DIRECT PAYMENT - Details already captured */
  const handleProceedToPayment = async () => {
    // Guard against double clicks
    if (isProcessing) return;
    setIsProcessing(true);

    try {
      // Get the first item's traveler details
      const firstItem = cartItems[0];
      if (!firstItem || !firstItem.travelerDetails) {
        alert("Missing traveler details. Please add items again.");
        setIsProcessing(false);
        return;
      }

      const { travelerDetails } = firstItem;

      /* Use ONE order id for both the saved booking and the payment so the
         payment-response handler can match and update the correct record. */
      const orderId = `ORDER_${Date.now()}`;

      /* SAVE BOOKING FIRST */
      const bookingPayload = {
        order_id: orderId,
        amount: totalPayable,
        name: travelerDetails.travellers[0]?.name || "Guest",
        phone: travelerDetails.phoneNumber,
        email: travelerDetails.email,
        traveller_count: travelerDetails.travellerCount,
        travellers: travelerDetails.travellers,
        package_name: firstItem.name,
        travel_date: travelerDetails.travelDate,
        type: firstItem.type || 'package',
        destination: firstItem.destination,
      };

      console.log("Saving booking:", bookingPayload);

      const bookingRes = await fetch(`${API_URL}/api/book`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(bookingPayload),
      });

      if (!bookingRes.ok) {
        alert("Booking could not be saved. Please try again.");
        setIsProcessing(false);
        return;
      }

      /* START PAYMENT */
      const payload = {
        order_id: orderId,
        amount: totalPayable.toFixed(2),
        billing_name: travelerDetails.travellers[0]?.name || "Guest",
        billing_email: travelerDetails.email || "guest@example.com",
        billing_tel: travelerDetails.phoneNumber,
      };

      console.log("Sending payment request:", payload);

      const form = document.createElement("form");
      form.method = "POST";
      form.action = `${PAYMENT_URL}/api/ccavenue-pay`;

      Object.entries(payload).forEach(([key, value]) => {
        const input = document.createElement("input");
        input.type = "hidden";
        input.name = key;
        input.value = value;
        form.appendChild(input);
      });

      document.body.appendChild(form);
      // Note: form.submit() will navigate away; do NOT reset isProcessing here
      // so the button stays disabled until the page leaves.
      form.submit();
    } catch (err) {
      console.error("Checkout error:", err);
      alert("Payment service unavailable. Please try again.");
      setIsProcessing(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="modern-cart-page">
        <motion.div 
          className="empty-cart"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="empty-icon">🛒</div>
          <h2>Your Cart is Empty</h2>
          <p>Start exploring our amazing travel packages and create unforgettable memories!</p>
          <Link to="/packages/" className="btn-primary-cart">
            Explore Packages
          </Link>
        </motion.div>
      </div>
    );
  }

  // Get traveler details from first item
  const travelerDetails = cartItems[0]?.travelerDetails;

  return (
    <div className="modern-cart-page">
      {/* Full-screen processing overlay to prevent any accidental interactions */}
      {isProcessing && (
        <div className="cart-processing-overlay" data-testid="cart-processing-overlay">
          <div className="cart-processing-modal">
            <FaSpinner className="cart-spinner cart-spinner-lg" />
            <h3>Processing your booking</h3>
            <p>Please don't close or refresh this page. Redirecting to secure payment...</p>
          </div>
        </div>
      )}

      <div className="cart-container">
        
        {/* Left Column - Cart Items */}
        <div className="cart-items-section">
          <h1 className="cart-title">Your Booking Cart</h1>
          
          {cartItems.map((item, index) => (
            <motion.div 
              key={item.id} 
              className="cart-item-card"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="item-image">
                {item.image && <img src={item.image} alt={item.name} />}
              </div>
              
              <div className="item-details">
                <h3 className="item-name">{item.name}</h3>
                <div className="item-meta">
                  <FaMapMarkerAlt style={{ marginRight: 6 }} />
                  {item.destination}
                </div>
                <div className="item-price">
                  <span className="price-label">Total Package:</span>
                  <span className="price-value">{item.price}</span>
                </div>
              </div>
              
              <button 
                className="remove-btn"
                onClick={() => removeFromCart(item.id)}
                title="Remove from cart"
              >
                <FaTrash />
              </button>
            </motion.div>
          ))}
        </div>

        {/* Right Column - Booking Summary */}
        <div className="booking-summary-section">
          
          {/* Traveler Information */}
          {travelerDetails && (
            <motion.div 
              className="traveler-info-card"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="card-header">
                <h3>📋 Traveler Information</h3>
                <span className="verified-badge">
                  <FaCheckCircle /> Verified
                </span>
              </div>
              
              <div className="info-grid">
                <div className="info-row">
                  <FaCalendar className="info-icon" />
                  <div>
                    <div className="info-label">Travel Date</div>
                    <div className="info-value">{new Date(travelerDetails.travelDate).toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}</div>
                  </div>
                </div>
                
                <div className="info-row">
                  <FaPhone className="info-icon" />
                  <div>
                    <div className="info-label">Contact Number</div>
                    <div className="info-value">{travelerDetails.phoneNumber}</div>
                  </div>
                </div>
                
                <div className="info-row">
                  <FaEnvelope className="info-icon" />
                  <div>
                    <div className="info-label">Email Address</div>
                    <div className="info-value">{travelerDetails.email}</div>
                  </div>
                </div>
                
                <div className="info-row">
                  <FaUsers className="info-icon" />
                  <div>
                    <div className="info-label">Number of Travelers</div>
                    <div className="info-value">{travelerDetails.travellerCount} {travelerDetails.travellerCount === 1 ? 'Person' : 'People'}</div>
                  </div>
                </div>
              </div>
              
              <div className="travelers-list">
                <div className="travelers-header">Travelers</div>
                {travelerDetails.travellers.map((traveler, i) => (
                  <div key={i} className="traveler-chip">
                    <FaUser />
                    <span>{traveler.name}, {traveler.age} years</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Payment Summary */}
          <motion.div 
            className="payment-summary-card"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3>💳 Payment Summary</h3>
            
            <div className="summary-row">
              <span>Number of Bookings</span>
              <span className="summary-value">{cartItems.length}</span>
            </div>

            {/* Per-item breakdown so full-payment (visa) vs advance is clear */}
            {cartItems.map((item) => (
              <div className="summary-row" key={item.id}>
                <span>
                  {item.name}
                  {item.type === "visa"
                    ? ` (full fee${getTravellerCount(item) > 1 ? ` × ${getTravellerCount(item)}` : ""})`
                    : " (advance)"}
                </span>
                <span className="summary-value">
                  ₹{getItemPayable(item).toLocaleString("en-IN")}
                </span>
              </div>
            ))}

            <div className="summary-divider"></div>

            <div className="summary-row total-row">
              <span>Total Payable Now</span>
              <span className="total-amount">₹{totalPayable.toLocaleString("en-IN")}</span>
            </div>

            <div className="payment-note">
              {isVisaOnly
                ? "💡 You're paying the full visa fee now. No balance will be due for your visa."
                : "💡 Advance shown for packages/hotels/cabs; remaining balance is collected later. Visa services are charged in full."}
            </div>
            
            <button 
              className="checkout-button"
              onClick={handleProceedToPayment}
              disabled={!travelerDetails || isProcessing}
              data-testid="cart-checkout-button"
            >
              {isProcessing ? (
                <>
                  <FaSpinner className="cart-spinner" style={{ marginRight: 8 }} />
                  Processing... Please wait
                </>
              ) : (
                <>
                  <FaCheckCircle style={{ marginRight: 8 }} />
                  Proceed to Secure Payment
                </>
              )}
            </button>
            
            <button 
              className="clear-cart-button"
              onClick={clearCart}
              disabled={isProcessing}
            >
              Clear All Items
            </button>
          </motion.div>

          {/* Security Badge */}
          <div className="security-badge">
            🔒 Secure payment powered by CCAvenue
          </div>
        </div>
      </div>
    </div>
  );
}

export default Cart;